import React from "react";
import moment, { Moment } from "moment";
import { Paper } from "@material-ui/core";
import { MultiInteractiveSpanGrid } from "./base/MultiInteractiveSpanGrid";
import { Span, SpanStyle } from "./base/utils";
import Body from "../typography/Body";

export type TimeSpanGridSpan = CustomDuration & SpanStyle;

export type TimeSpanGridProps = {
  end?: moment.Moment;
  isGroupDisabled?: (v: Moment, color?: string) => boolean;
  isCellDisabled?: (date: Moment, color: string) => boolean;
  skipDisabledSpans?: boolean;
  renderCellContent?: (date: Moment, color: string) => string;
  onChange?: (v: CustomDuration[]) => void;
  onClick?: (week: Date) => void;
  onHover?: (date: Date, e: React.MouseEvent) => void;
  readonly?: boolean;
  spans: TimeSpanGridSpan[];
  start: moment.Moment;
  value?: CustomDuration[];
};

export const timeSpanGridFactory =
  (
    unitOfTime: moment.unitOfTime.Diff,
    minItems: number,
    render: (v: moment.Moment) => React.ReactNode
  ) =>
  (props: TimeSpanGridProps) => {
    const {
      start,
      end,
      isGroupDisabled,
      isCellDisabled,
      renderCellContent,
      value: _value,
      onClick,
      onChange,
      spans,
      readonly
    } = props;

    const itemCount = end ? Math.ceil(-start.diff(end, unitOfTime, true)) : minItems;
    const cells: moment.Moment[] = [];
    const disabledCellsGroupIndexes: number[] = [];

    [...Array(itemCount)].forEach((_, i) => {
      const cell = start.clone().add(i, unitOfTime);
      cells.push(cell);

      if (isGroupDisabled && isGroupDisabled(cell)) {
        disabledCellsGroupIndexes.push(i);
      }
    });

    const durationToSpan = ({ From, To }: CustomDuration) => ({
      start: -start.diff(From, unitOfTime),
      end: start.isAfter(To) ? -1 : -start.diff(To, unitOfTime)
    });

    const spanToDuration = (span: Span) => ({
      From: start.clone().add(span.start, unitOfTime).toDate(),
      To: start.clone().add(span.end, unitOfTime).toDate()
    });

    const dateInCell = (index: number) => start.clone().add(index, unitOfTime);

    const value = (_value || []).map(durationToSpan);

    const _spans = spans.map((x) => ({
      ...x,
      ...durationToSpan(x)
    }));

    return (
      <MultiInteractiveSpanGrid
        readonly={readonly}
        renderStyle="SolidSoft"
        cells={cells}
        disabledCellsGroupIndexes={disabledCellsGroupIndexes}
        contentSelector={render}
        renderCellContent={(index, color) =>
          renderCellContent?.(dateInCell(index), color)
        }
        isCellDisabled={(index, color) => isCellDisabled?.(dateInCell(index), color)}
        renderPrefix={({ current, previous }) => {
          const year = current.isoWeekYear();

          if (
            (previous && current.isoWeekYear() !== previous.isoWeekYear()) ||
            !previous
          ) {
            return (
              <Paper style={styles.yearPrefix}>
                <Body>{year}</Body>
              </Paper>
            );
          }

          return null;
        }}
        onClick={(span) => {
          onClick && onClick(spanToDuration(span).From);
        }}
        onSelect={(s) => {
          const v = s.map(spanToDuration);
          onChange && onChange(v);
        }}
        onHover={(cellNumber, e) => {
          props.onHover?.(cells[cellNumber].toDate(), e);
        }}
        value={value}
        spans={_spans}
        skipDisabledSpans={props.skipDisabledSpans}
      />
    );
  };

const styles = {
  yearPrefix: { padding: "0 6px", backgroundColor: "darkgray", color: "white" }
};
